import { useState } from 'react';
import { isIOS } from './format';

// iOSのnavigator.shareはタップ直後の一時的なユーザーアクティベーション（約5秒）を
// 要求する。生成がこの枠に収まれば、そのまま共有シート（「ファイルに保存」を含む）
// まで一気に進められる。収まらなかった場合だけ「PDFを保存」ボタンに退避して、
// そのタップを新しいアクティベーションとして共有する（shareOrDefer）。
//
// 当初は常に2段階（生成 → ボタンのタップで共有）にしていたが、これは画像を原寸の
// まま埋め込んでいて生成に数十秒かかっていた頃の設計。downscaleForPdfの導入で
// 生成が大幅に短くなったため、通常は1タップで済むようにしている。
// 「2段階UIが分かりにくい」という利用者からの指摘への対応。
//
// 戻り値は保存まで到達したかどうか。呼び出し側が完了時だけ追加の後始末
// （照合結果のクリア等）を行えるようにするため（キャンセル時に消えてしまわないように）。

// 実際の共有／ダウンロード。結果を呼び出し側が区別できるよう文字列で返す。
//   'saved'     共有シートまたは直接ダウンロードで保存まで完了した
//   'cancelled' 利用者が共有シートを閉じた（保存していない）
//   'expired'   ユーザーアクティベーションの期限切れ等で共有シートを開けなかった（押し直しで再試行）
//   'unsupported' iOSでファイルの共有に対応していない（ダウンロードには切り替えない）
//   'failed'    iOSで押し直しても直らない理由で共有に失敗した（案内済み。PDFは残さない）
//   'cancelled' と 'expired' だけが、生成済みPDFを残して押し直しで再試行する対象
async function sharePdfBlob(blob, filename) {
  const file = new File([blob], `${filename}.pdf`, { type: 'application/pdf' });
  const downloadBlob = () => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // iOSでは直接ダウンロードに切り替えない。iOS Safariはblob URLのdownload属性を
  // 無視し、保存する代わりにアプリの画面そのものをPDFビューアに置き換える。
  // ホーム画面から起動したアプリ（display:standalone）には戻る手段が無いため、
  // 検査画面に戻れなくなる（iPhone SEで共有シートをキャンセルした後に実際に発生）。
  // iOSでは共有シートだけを使い、開けなかった場合は生成済みPDFを残して押し直してもらう
  const ios = isIOS();

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'saved';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
      // アクティベーション切れ（NotAllowedError）。iOS Safariはblob URLの
      // download属性を無視するため、ここでダウンロードにフォールバックしても
      // PDFがビューアで表示されるだけで保存されない。改めてタップしてもらう
      if (e?.name === 'NotAllowedError' && ios) return 'expired';
      // iOSは上記の理由でダウンロードに切り替えない。前の共有シートが閉じきる前に
      // 開き直した場合（InvalidStateError）は押し直せば開けるので、PDFを残して再試行。
      // それ以外（ファイルを共有シートが受け付けない等）は押し直しても直らないため、
      // 案内を出してPDFは残さない（残すと同じ失敗を無言で繰り返すことになる）
      if (ios) {
        if (e?.name === 'InvalidStateError') {
          console.error('[sharePdfBlob] share sheet busy on iOS, keeping the PDF for retry:', e);
          return 'expired';
        }
        console.error('[sharePdfBlob] share failed on iOS:', e);
        alert('PDFを保存できませんでした。件数を減らして出力し直すか、PCから出力してください。');
        return 'failed';
      }
      // canShareがtrueでも共有シートの起動自体が失敗する端末があるため、
      // 直接ダウンロードにフォールバックする
      console.error('[sharePdfBlob] share failed, falling back to direct download:', e);
      downloadBlob();
      return 'saved';
    }
  }
  if (ios) {
    alert('この端末ではPDFを共有シートで保存できません。iOSを最新版に更新するか、PCから検査履歴のPDFを出力してください。');
    return 'unsupported';
  }
  downloadBlob();
  return 'saved';
}

// 生成済みPDFを残して押し直しで再試行する対象か
export const isRetryable = (result) => result === 'cancelled' || result === 'expired';

export function usePendingPdfShare() {
  const [pendingPdf, setPendingPdf] = useState(null); // { blob, filename }

  // 生成直後にそのまま共有まで進める。保存できなかった場合（キャンセル・
  // アクティベーション切れ）は「PDFを保存」ボタンから再試行できるよう生成済み
  // PDFを残す。iOS以外はそもそも共有シートを使わないので即座にダウンロードされる
  const shareOrDefer = async (blob, filename) => {
    const result = await sharePdfBlob(blob, filename);
    if (result === 'saved') return true;
    // 押し直しで保存できる場合だけ残す。'unsupported'・'failed' で残すと、押すたびに
    // 同じ案内が出るだけで先に進めず、検査結果もクリアできなくなる
    if (isRetryable(result)) setPendingPdf({ blob, filename });
    return false;
  };

  // 「PDFを保存」ボタン（退避した場合のみ表示される）から呼ぶ
  const sharePendingPdf = async () => {
    if (!pendingPdf) return false;
    const result = await sharePdfBlob(pendingPdf.blob, pendingPdf.filename);
    if (result === 'saved') { setPendingPdf(null); return true; }
    // キャンセル・一時的な失敗は生成済みPDFを残して再試行できるようにする
    if (!isRetryable(result)) setPendingPdf(null);
    return false;
  };

  return { pendingPdf, setPendingPdf, shareOrDefer, sharePendingPdf };
}
